import React, { useEffect, useState, useRef } from 'react';
import { MdOutlineAnalytics, MdOutlinePsychology, MdOutlineGroups, MdOutlineHeadphones, MdSecurity, MdAutoGraph, MdSpeed, MdStars, MdTrendingUp, MdLeaderboard } from 'react-icons/md';
import { FaRobot, FaChartLine, FaFootballBall, FaTrophy, FaChartBar, FaCalculator, FaBrain, FaLock, FaCheck, FaRocket, FaCrown, FaMedal, FaGem, FaFireAlt, FaFistRaised, FaArrowRight } from 'react-icons/fa';
import futLogo from '@/assets/svgs/logo.svg';
import miyagiImage from '@/assets/miyagi/1.jpg';
import partidaVideo from '@/assets/miyagi/partida-futebol.mp4';
import { FaChartBar as FaChartBarIcon, FaDatabase, FaBolt, FaUsers, FaHistory, FaPlay, FaGamepad } from 'react-icons/fa';
import { MdSmartToy, MdLiveTv, MdSportsScore, MdPersonSearch } from 'react-icons/md';
import { RiAiGenerate } from 'react-icons/ri';
import { SiOpenai } from 'react-icons/si';
import { IoAnalyticsSharp } from 'react-icons/io5';
import { useNavigate } from 'react-router-dom';
import { Avatar } from '@mui/material';
import { motion, AnimatePresence, useScroll, useTransform, useSpring, useInView } from 'framer-motion';
import { FaStar } from 'react-icons/fa';

import useNewLp from './useNewLp';
import * as S from './newlp.styles';

// Componente de animação de scroll reveal
interface ScrollRevealProps {
  children: React.ReactNode;
  delay?: number;
}

const ScrollReveal: React.FC<ScrollRevealProps> = ({ children, delay = 0 }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-20% 0px -20% 0px" });

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 50, rotateX: 20, perspective: 1000 }}
      animate={isInView ? { opacity: 1, y: 0, rotateX: 0 } : { opacity: 0, y: 50, rotateX: 20 }}
      transition={{
        duration: 0.8,
        delay: delay,
        ease: [0.17, 0.55, 0.55, 1]
      }}
      style={{
        transformStyle: "preserve-3d"
      }}
    >
      {children}
    </motion.div>
  );
};

const GlobalStyles = S.GlobalStyle;

const NewLp: React.FC = () => {
  const navigate = useNavigate();
  const {
    currentTestimonial,
    setCurrentTestimonial,
    testimonials,
    handleRegisterClick,
    handleLoginClick
  } = useNewLp();

  const [particles, setParticles] = useState<{ x: number, y: number, size: number }[]>([]);
  const buttonRef = useRef<HTMLButtonElement>(null);

  // Função para criar efeito de partículas no botão
  const createParticles = (e: React.MouseEvent<HTMLButtonElement>) => {
    if (buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;

      const circle = document.createElement('div');
      circle.className = 'pulse-circle';
      circle.style.width = '100px';
      circle.style.height = '100px';
      circle.style.left = `${x}px`;
      circle.style.top = `${y}px`;
      buttonRef.current.appendChild(circle);

      setTimeout(() => {
        if (circle.parentElement) {
          circle.parentElement.removeChild(circle);
        }
      }, 1500);
    }
  };

  // Adicionar efeito de cursor personalizado para toda a página
  useEffect(() => {
    const cursor = document.createElement('div');
    cursor.className = 'custom-cursor';
    document.body.appendChild(cursor);

    const cursorInner = document.createElement('div');
    cursorInner.className = 'cursor-inner';
    document.body.appendChild(cursorInner);

    document.addEventListener('mousemove', (e) => {
      cursor.style.transform = `translate(${e.clientX}px, ${e.clientY}px)`;
      cursorInner.style.transform = `translate(${e.clientX}px, ${e.clientY}px)`;
    });

    // Adicionar efeito de hover em todos os elementos clicáveis
    const clickables = document.querySelectorAll('button, a');
    clickables.forEach((el) => {
      el.addEventListener('mouseenter', () => {
        cursor.classList.add('cursor-hover');
        cursorInner.classList.add('cursor-inner-hover');
      });
      el.addEventListener('mouseleave', () => {
        cursor.classList.remove('cursor-hover');
        cursorInner.classList.remove('cursor-inner-hover');
      });
    });

    return () => {
      document.body.removeChild(cursor);
      document.body.removeChild(cursorInner);
    };
  }, []);

  const handleDemoClick = () => {
    navigate('/home-lp');
  };

  // Animações de scroll
  const { scrollYProgress } = useScroll();
  const scaleX = useSpring(scrollYProgress, {
    stiffness: 100,
    damping: 30,
    restDelta: 0.001
  });

  return (
    <>
      <GlobalStyles />
      <motion.div
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          height: '4px',
          background: 'linear-gradient(90deg, #2af497, #1ce488)',
          transformOrigin: '0%',
          scaleX
        }}
      />
      <S.Container>
        <S.Header>
          <img src={futLogo} alt="Precision" />
          <S.LoginButton onClick={handleLoginClick}>
            Login
          </S.LoginButton>
        </S.Header>

        <S.HeroSection>
          <S.VideoBackground>
            <video autoPlay loop muted playsInline>
              <source src={partidaVideo} type="video/mp4" />
            </video>

            {/* Overlay com efeito de escaneamento */}
            <div className="scan-overlay" style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'linear-gradient(to bottom, rgba(0,0,0,0.7), rgba(0,0,0,0.5))',
              zIndex: 1,
              pointerEvents: 'none',
            }}>
              {/* Definição da animação scanLine */}
              <style>
                {`
                  @keyframes scanLine {
                    0% { top: 0%; }
                    100% { top: 100%; }
                  }
                `}
              </style>

              {/* Linhas de escaneamento */}
              <div className="scan-line" style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                height: '2px',
                background: 'linear-gradient(to right, transparent, rgba(42,244,151,0.8), transparent)',
                boxShadow: '0 0 10px rgba(42,244,151,0.8)',
                animation: 'scanLine 3s linear infinite',
                zIndex: 2,
              }} />

              {/* Partículas flutuantes */}
              <div className="particles">
                {Array.from({ length: 30 }).map((_, index) => (
                  <div key={index} className="particle" style={{
                    position: 'absolute',
                    width: `${Math.random() * 5 + 2}px`,
                    height: `${Math.random() * 5 + 2}px`,
                    background: `rgba(42, 244, 151, ${0.3 + Math.random() * 0.4})`,
                    borderRadius: '50%',
                    top: `${Math.random() * 100}%`,
                    left: `${Math.random() * 100}%`,
                    animation: `floatY ${5 + Math.random() * 10}s infinite ease-in-out`,
                    animationDelay: `${Math.random() * 5}s`,
                    zIndex: 2,
                  }} />
                ))}
              </div>

              {/* Grade de fundo estilo Tron */}
              <div className="grid-background" style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundImage: 'linear-gradient(rgba(42,244,151,0.05) 1px, transparent 1px), linear-gradient(90deg, rgba(42,244,151,0.05) 1px, transparent 1px)',
                backgroundSize: '30px 30px',
                animation: 'gridMove 20s infinite linear',
                zIndex: 1,
                opacity: 0.3,
              }} />
            </div>
          </S.VideoBackground>

          <S.HeroContent>
            <div className="content-wrapper">
              <h1>Transforme Dados em Lucros: Algoritmos que Preveem Resultados com 95% de Precisão</h1>
              <p>Acesso gratuito à plataforma que já gerou mais de R$ 5.7 milhões para milhares de usuários utilizando inteligência artificial avançada. Comece hoje mesmo sem precisar de cartão de crédito.</p>
              <div className="cta-buttons">
                <S.CTAButton onClick={handleRegisterClick}>
                  CRIAR CONTA GRÁTIS AGORA
                </S.CTAButton>
                <S.SecondaryButton onClick={handleDemoClick}>
                  VER DEMONSTRAÇÃO
                </S.SecondaryButton>
              </div>
            </div>

            <div className="floating-cards-container">
              <S.FloatingCard delay="0s" style={{ top: '15%', left: '5%' }}>
                <FaRobot />
                <div>
                  <h4>IA Avançada</h4>
                  <p>Análise em tempo real</p>
                </div>
              </S.FloatingCard>

              <S.FloatingCard delay="1s" style={{ top: '15%', right: '5%' }}>
                <FaChartLine />
                <div>
                  <h4>95% Precisão</h4>
                  <p>Resultados comprovados</p>
                </div>
              </S.FloatingCard>

              <S.FloatingCard delay="2s" style={{ bottom: '15%', left: '5%' }}>
                <FaFootballBall />
                <div>
                  <h4>+1000 Jogos</h4>
                  <p>Analisados diariamente</p>
                </div>
              </S.FloatingCard>

              <S.FloatingCard delay="3s" style={{ bottom: '15%', right: '5%' }}>
                <FaTrophy />
                <div>
                  <h4>+50.000</h4>
                  <p>Apostadores lucrativos</p>
                </div>
              </S.FloatingCard>
            </div>
          </S.HeroContent>

          {/* Anéis de estatísticas */}
          <S.StatsRing style={{ top: '15%', left: '10%' }} />
          <S.StatsRing style={{ bottom: '15%', right: '10%' }} />
        </S.HeroSection>

        <ScrollReveal>
          <S.FeaturesSection>
            <S.FeaturesGrid>
              {/* Cada feature card com delay incremental */}
              <ScrollReveal delay={0.1}>
                <S.FeatureCard>
                  <MdOutlineAnalytics size={40} />
                  <h3>Análise Preditiva em Tempo Real</h3>
                  <p>Tome decisões informadas com métricas avançadas que antecipam o desenrolar das partidas antes mesmo dos bookmakers.</p>
                </S.FeatureCard>
              </ScrollReveal>

              <ScrollReveal delay={0.2}>
                <S.FeatureCard>
                  <MdOutlinePsychology size={40} />
                  <h3>Apostas Guiadas por IA</h3>
                  <p>Nossos algoritmos identificam oportunidades de alto valor que passam despercebidas para apostadores comuns.</p>
                </S.FeatureCard>
              </ScrollReveal>

              <ScrollReveal delay={0.3}>
                <S.FeatureCard>
                  <MdOutlineGroups size={40} />
                  <h3>Programa de Afiliados Premium</h3>
                  <p>Ganhe até 50% de comissão recorrente ao compartilhar sua experiência de sucesso com outros apostadores.</p>
                </S.FeatureCard>
              </ScrollReveal>

              <ScrollReveal delay={0.4}>
                <S.FeatureCard>
                  <MdOutlineHeadphones size={40} />
                  <h3>Suporte VIP Personalizado</h3>
                  <p>Atendimento exclusivo via Telegram para garantir que você maximize seus resultados desde o primeiro dia.</p>
                </S.FeatureCard>
              </ScrollReveal>
            </S.FeaturesGrid>
          </S.FeaturesSection>
        </ScrollReveal>

        <ScrollReveal>
          <S.TestimonialsSection>
            <S.TestimonialSlider>
              <AnimatePresence mode="wait">
                {testimonials.map((testimonial, index) => (
                  <S.TestimonialCard
                    key={testimonial.name}
                    isActive={index === currentTestimonial}
                    isNext={index > currentTestimonial}
                  >
                    <div className="testimonial-header">
                      <div className="avatar-container">
                        <Avatar
                          src={`https://api.dicebear.com/7.x/avataaars/svg?seed=${testimonial.seed}`}
                          sx={{
                            width: 80,
                            height: 80,
                            border: '2px solid rgba(42, 244, 151, 0.3)',
                            backgroundColor: 'rgba(42, 244, 151, 0.1)'
                          }}
                        />
                      </div>
                      <div className="user-info">
                        <h4>{testimonial.name}</h4>
                        <div className="rating">
                          {Array.from({ length: testimonial.rating }).map((_, i) => (
                            <FaStar key={i} />
                          ))}
                        </div>
                      </div>
                    </div>
                    <p>{testimonial.text}</p>
                  </S.TestimonialCard>
                ))}
              </AnimatePresence>
            </S.TestimonialSlider>
            <S.TestimonialDots>
              {testimonials.map((_, index) => (
                <S.Dot
                  key={index}
                  active={index === currentTestimonial}
                  onClick={() => setCurrentTestimonial(index)}
                />
              ))}
            </S.TestimonialDots>
          </S.TestimonialsSection>
        </ScrollReveal>

        <ScrollReveal>
          <S.CEOSection>
            {/* Elemento de fundo psicodélico */}
            <div className="psychedelic-bg" style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'radial-gradient(circle at center, rgba(42,244,151,0.1) 0%, rgba(0,0,0,0) 70%)',
              zIndex: 0,
              animation: 'psychedelicPulse 8s infinite alternate',
            }} />

            {/* Elementos flutuantes */}
            <div className="floating-icon" style={{
              position: 'absolute',
              top: '15%',
              left: '10%',
              color: 'rgba(42,244,151,0.3)',
              fontSize: '3rem',
              animation: 'floatY 6s ease-in-out infinite',
              zIndex: 1,
            }}>
              <FaChartLine />
            </div>
            <div className="floating-icon" style={{
              position: 'absolute',
              top: '70%',
              right: '15%',
              color: 'rgba(42,244,151,0.3)',
              fontSize: '3rem',
              animation: 'floatY 8s ease-in-out infinite',
              animationDelay: '2s',
              zIndex: 1,
            }}>
              <SiOpenai />
            </div>
            <div className="floating-icon" style={{
              position: 'absolute',
              bottom: '20%',
              left: '20%',
              color: 'rgba(42,244,151,0.3)',
              fontSize: '3rem',
              animation: 'floatY 7s ease-in-out infinite',
              animationDelay: '1s',
              zIndex: 1,
            }}>
              <IoAnalyticsSharp />
            </div>

            {/* Título principal agora fora das divs de conteúdo, para que fique acima de tudo */}
            <div style={{
              gridColumn: '1 / -1',
              textAlign: 'center',
              marginBottom: '2rem',
              zIndex: 5
            }}>
              <h2 style={{
                fontSize: '3rem',
                color: '#2af497',
                lineHeight: '1.2',
                position: 'relative',
                textShadow: '0 0 20px rgba(42, 244, 151, 0.5)',
                display: 'inline-block'
              }}>Conheça Miyagi Nakamoto</h2>
            </div>

            <S.CEOContent>
              <div className="stats-container">
                <div className="stat-item">
                  <div className="stat-title">Precisão</div>
                  <div className="stat-value">98%</div>
                  <div className="stat-bar" style={{ "--progress": "98%" } as React.CSSProperties}></div>
                </div>
                <div className="stat-item">
                  <div className="stat-title">Análise de Dados</div>
                  <div className="stat-value">95%</div>
                  <div className="stat-bar" style={{ "--progress": "95%" } as React.CSSProperties}></div>
                </div>
                <div className="stat-item">
                  <div className="stat-title">Trading Esportivo</div>
                  <div className="stat-value">99%</div>
                  <div className="stat-bar" style={{ "--progress": "99%" } as React.CSSProperties}></div>
                </div>
                <div className="stat-item">
                  <div className="stat-title">Inteligência Artificial</div>
                  <div className="stat-value">97%</div>
                  <div className="stat-bar" style={{ "--progress": "97%" } as React.CSSProperties}></div>
                </div>
              </div>

              <p>
                <strong>Cansado de sistemas que prometem muito e entregam pouco?</strong> Miyagi Nakamoto é trader profissional e especialista em análise de dados esportivos que transformou o mercado de apostas com sua abordagem analítica revolucionária.
              </p>
              <p>
                Após 5 anos desenvolvendo um sistema proprietário que combina IA avançada com análise técnica, Miyagi conseguiu o que parecia impossível: <strong>um modelo preditivo com mais de 95% de acurácia</strong>, testado em mais de 200.000 partidas.
              </p>
              <p>
                <strong>Agora, exclusivamente pela Precision</strong>, você tem acesso ao mesmo sistema que já gerou mais de R$ 5,7 milhões em lucros para nossa comunidade de apostadores inteligentes. Não é sorte, é ciência de dados aplicada.
              </p>

              <S.MiyagiButton onClick={handleRegisterClick}>
                QUERO ACESSO AGORA <span style={{ fontSize: '0.8em', marginLeft: '5px' }}>(Vagas Limitadas)</span>
              </S.MiyagiButton>
            </S.CEOContent>

            <S.CEOImageWrapper>
              <S.CEOImageContainer>
                <img src={miyagiImage} alt="Miyagi Nakamoto - CEO Precision" />
                <div className="legendary-aura"></div>
                <S.CardHeader>
                  <S.CardPosition>CEO</S.CardPosition>
                  <S.CardRating>99</S.CardRating>
                </S.CardHeader>
                <S.CardName>Miyagi Nakamoto</S.CardName>
                <S.CardBadges className="card-badges">
                  <S.CardBadgeItem>
                    <div className="badge-icon"><FaCrown /></div>
                    <div className="badge-name">Master Trader</div>
                  </S.CardBadgeItem>
                  <S.CardBadgeItem>
                    <div className="badge-icon"><FaMedal /></div>
                    <div className="badge-name">Análise Dados</div>
                  </S.CardBadgeItem>
                  <S.CardBadgeItem>
                    <div className="badge-icon"><FaGem /></div>
                    <div className="badge-name">Trading Elite</div>
                  </S.CardBadgeItem>
                  <S.CardBadgeItem>
                    <div className="badge-icon"><FaRocket /></div>
                    <div className="badge-name">9000+</div>
                  </S.CardBadgeItem>
                </S.CardBadges>
              </S.CEOImageContainer>
            </S.CEOImageWrapper>
          </S.CEOSection>
        </ScrollReveal>

        <ScrollReveal>
          <S.PricingSection>
            {/* Elementos de fundo animados */}
            <div className="sparkles" style={{ top: '20%', left: '15%' }} />
            <div className="sparkles" style={{ top: '60%', left: '85%', animationDelay: '1.5s' }} />
            <div className="sparkles" style={{ top: '35%', left: '65%', animationDelay: '0.7s' }} />
            <div className="sparkles" style={{ top: '80%', left: '35%', animationDelay: '2s' }} />

            <S.GlowingOrb size="250px" top="10%" left="5%" delay="0s" />
            <S.GlowingOrb size="200px" top="60%" left="75%" delay="2s" />

            <h2>ACESSO GRATUITO POR TEMPO LIMITADO!</h2>
            <p>Estamos oferecendo 500 vagas de acesso gratuito à nossa plataforma completa. <strong>Restam apenas <span style={{ color: '#ff3e3e' }}>73 vagas</span> disponíveis hoje!</strong></p>

            <div style={{ margin: '30px 0', padding: '15px 25px', background: 'rgba(255,255,255,0.1)', borderRadius: '8px', marginLeft: 'auto', marginRight: 'auto', maxWidth: '650px' }}>
              <p style={{ fontWeight: 'bold', fontSize: '1.1em', marginBottom: '10px' }}>O QUE VOCÊ RECEBE GRATUITAMENTE:</p>
              <ul style={{ textAlign: 'left', listStyle: 'none', padding: 0 }}>
                <li style={{ margin: '10px 0', display: 'flex', alignItems: 'center' }}>
                  <span style={{ color: '#2af497', marginRight: '10px' }}>✓</span> Acesso completo à análise de jogos em tempo real
                </li>
                <li style={{ margin: '10px 0', display: 'flex', alignItems: 'center' }}>
                  <span style={{ color: '#2af497', marginRight: '10px' }}>✓</span> Previsões automáticas com 95% de precisão
                </li>
                <li style={{ margin: '10px 0', display: 'flex', alignItems: 'center' }}>
                  <span style={{ color: '#2af497', marginRight: '10px' }}>✓</span> Base de dados históricos de mais de 200.000 partidas
                </li>
                <li style={{ margin: '10px 0', display: 'flex', alignItems: 'center' }}>
                  <span style={{ color: '#2af497', marginRight: '10px' }}>✓</span> Suporte via Telegram com nossa equipe de especialistas
                </li>
              </ul>
            </div>

            <S.RegisterButton onClick={handleRegisterClick}>
              CRIAR MINHA CONTA GRÁTIS AGORA
            </S.RegisterButton>

            <p style={{ fontSize: '0.9em', marginTop: '15px', color: 'rgba(255,255,255,0.7)' }}>Sem cartão de crédito • Cadastro em menos de 2 minutos • Acesso imediato</p>
          </S.PricingSection>
        </ScrollReveal>

        <ScrollReveal>
          <S.DataTimelineSection>
            <S.LightRays />
            <div className="motion-bg" style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'linear-gradient(125deg, rgba(42,244,151,0.03) 0%, rgba(42,244,151,0.03) 25%, rgba(0,0,0,0) 50%, rgba(42,244,151,0.03) 75%, rgba(42,244,151,0.03) 100%)',
              backgroundSize: '400% 400%',
              animation: 'gradientAnimation 15s ease infinite',
              zIndex: 0,
            }} />

            {/* Efeito LSD em todo o fundo da seção */}
            <div className="lsd-effect-overlay" style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'radial-gradient(circle at center, rgba(42,244,151,0.05) 0%, rgba(0,0,0,0) 70%)',
              animation: 'lsdEffect 20s infinite linear',
              zIndex: 0,
              pointerEvents: 'none',
            }} />

            {/* Grid de fundo estilo Tron */}
            <div className="grid-bg" style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundImage: 'linear-gradient(rgba(42,244,151,0.07) 1px, transparent 1px), linear-gradient(90deg, rgba(42,244,151,0.07) 1px, transparent 1px)',
              backgroundSize: '20px 20px',
              backgroundPosition: 'center center',
              animation: 'gridMove 25s infinite linear',
              zIndex: 0,
            }} />

            {/* Partículas flutuantes */}
            <div className="floating-particles">
              {Array.from({ length: 15 }).map((_, index) => (
                <div key={index} className="particle" style={{
                  position: 'absolute',
                  width: `${Math.random() * 10 + 5}px`,
                  height: `${Math.random() * 10 + 5}px`,
                  top: `${Math.random() * 100}%`,
                  left: `${Math.random() * 100}%`,
                  background: `rgba(42, 244, 151, ${Math.random() * 0.3 + 0.1})`,
                  borderRadius: '50%',
                  animation: `floatY ${Math.random() * 8 + 5}s infinite ease-in-out`,
                  animationDelay: `${Math.random() * 5}s`,
                  zIndex: 1,
                }} />
              ))}
            </div>

            <S.TimelineContainer>
              <S.TimelineHeading>
                <h2>Sistema Avançado de Dados</h2>
                <p>Explore nosso arsenal completo de ferramentas de análise esportiva potenciadas por inteligência artificial</p>
              </S.TimelineHeading>

              <S.TimelineMap>
                <S.TimelineNode style={{ "--index": 0 } as React.CSSProperties}>
                  <div className="badge">TEMPO REAL</div>
                  <div className="glow-effect"></div>
                  <div className="node-content">
                    <div className="node-icon">
                      <MdLiveTv />
                    </div>
                    <h3 className="node-title">Partidas ao Vivo</h3>
                    <p className="node-description">
                      Acompanhe partidas em tempo real com dados atualizados constantemente e análises dinâmicas que evoluem a cada minuto do jogo.
                    </p>
                    <div className="progress-bar">
                      <div className="progress"></div>
                    </div>
                    <div className="node-stats">
                      <div className="stat-item">
                        <div className="stat-value">98%</div>
                        <div className="stat-label">Precisão</div>
                      </div>
                      <div className="stat-item">
                        <div className="stat-value">5ms</div>
                        <div className="stat-label">Atualização</div>
                      </div>
                    </div>
                  </div>
                  <div className="radar-scan"></div>
                </S.TimelineNode>

                <S.TimelineNode style={{ "--index": 1 } as React.CSSProperties}>
                  <div className="badge">INTELIGENTE</div>
                  <div className="glow-effect"></div>
                  <div className="node-content">
                    <div className="node-icon">
                      <MdOutlineAnalytics />
                    </div>
                    <h3 className="node-title">Análises ao Vivo</h3>
                    <p className="node-description">
                      Algoritmos de IA analisam padrões de jogo e identificam oportunidades enquanto a partida acontece, fornecendo insights instantâneos.
                    </p>
                    <div className="progress-bar">
                      <div className="progress"></div>
                    </div>
                    <div className="node-stats">
                      <div className="stat-item">
                        <div className="stat-value">146+</div>
                        <div className="stat-label">Métricas</div>
                      </div>
                      <div className="stat-item">
                        <div className="stat-value">99%</div>
                        <div className="stat-label">Confiança</div>
                      </div>
                    </div>
                  </div>
                  <div className="radar-scan"></div>
                </S.TimelineNode>

                <S.TimelineNode style={{ "--index": 2 } as React.CSSProperties}>
                  <div className="badge">HISTÓRICO</div>
                  <div className="glow-effect"></div>
                  <div className="node-content">
                    <div className="node-icon">
                      <FaHistory />
                    </div>
                    <h3 className="node-title">Histórico de Partidas</h3>
                    <p className="node-description">
                      Acesse um banco de dados completo com mais de 200.000 partidas históricas e estatísticas detalhadas para análise profunda.
                    </p>
                    <div className="progress-bar">
                      <div className="progress"></div>
                    </div>
                    <div className="node-stats">
                      <div className="stat-item">
                        <div className="stat-value">10</div>
                        <div className="stat-label">Anos</div>
                      </div>
                      <div className="stat-item">
                        <div className="stat-value">200k+</div>
                        <div className="stat-label">Jogos</div>
                      </div>
                    </div>
                  </div>
                  <div className="radar-scan"></div>
                </S.TimelineNode>

                <S.TimelineNode style={{ "--index": 3 } as React.CSSProperties}>
                  <div className="badge">ATLETAS</div>
                  <div className="glow-effect"></div>
                  <div className="node-content">
                    <div className="node-icon">
                      <MdPersonSearch />
                    </div>
                    <h3 className="node-title">Histórico de Jogadores</h3>
                    <p className="node-description">
                      Perfis detalhados de jogadores com estatísticas individuais, histórico de desempenho e análises preditivas de performance.
                    </p>
                    <div className="progress-bar">
                      <div className="progress"></div>
                    </div>
                    <div className="node-stats">
                      <div className="stat-item">
                        <div className="stat-value">50k+</div>
                        <div className="stat-label">Jogadores</div>
                      </div>
                      <div className="stat-item">
                        <div className="stat-value">320+</div>
                        <div className="stat-label">Atributos</div>
                      </div>
                    </div>
                  </div>
                  <div className="radar-scan"></div>
                </S.TimelineNode>

                <S.TimelineNode style={{ "--index": 4 } as React.CSSProperties}>
                  <div className="badge">PODEROSO</div>
                  <div className="glow-effect"></div>
                  <div className="node-content">
                    <div className="node-icon">
                      <IoAnalyticsSharp />
                    </div>
                    <h3 className="node-title">Análises Estatísticas</h3>
                    <p className="node-description">
                      Ferramentas avançadas de visualização e análise de dados para identificar tendências, padrões e oportunidades com alto grau de precisão.
                    </p>
                    <div className="progress-bar">
                      <div className="progress"></div>
                    </div>
                    <div className="node-stats">
                      <div className="stat-item">
                        <div className="stat-value">97%</div>
                        <div className="stat-label">Precisão</div>
                      </div>
                      <div className="stat-item">
                        <div className="stat-value">75+</div>
                        <div className="stat-label">Modelos</div>
                      </div>
                    </div>
                  </div>
                  <div className="radar-scan"></div>
                </S.TimelineNode>

                <S.TimelineNode style={{ "--index": 5 } as React.CSSProperties}>
                  <div className="badge">AFILIADOS</div>
                  <div className="glow-effect"></div>
                  <div className="node-content">
                    <div className="node-icon">
                      <FaUsers />
                    </div>
                    <h3 className="node-title">Indique e Ganhe</h3>
                    <p className="node-description">
                      Programa de afiliados com comissões generosas, dashboard personalizado de acompanhamento e ferramentas de marketing exclusivas.
                    </p>
                    <div className="progress-bar">
                      <div className="progress"></div>
                    </div>
                    <div className="node-stats">
                      <div className="stat-item">
                        <div className="stat-value">50%</div>
                        <div className="stat-label">Comissão</div>
                      </div>
                      <div className="stat-item">
                        <div className="stat-value">24h</div>
                        <div className="stat-label">Pagamento</div>
                      </div>
                    </div>
                  </div>
                  <div className="radar-scan"></div>
                </S.TimelineNode>

                <S.TimelineNode style={{ "--index": 6 } as React.CSSProperties}>
                  <div className="badge">IA AVANÇADA</div>
                  <div className="glow-effect"></div>
                  <div className="node-content">
                    <div className="node-icon">
                      <SiOpenai />
                    </div>
                    <h3 className="node-title">Bilhetes Inteligentes</h3>
                    <p className="node-description">
                      Sistema de IA gera bilhetes otimizados com base em milhões de simulações e análises de probabilidade em tempo real.
                    </p>
                    <div className="progress-bar">
                      <div className="progress"></div>
                    </div>
                    <div className="node-stats">
                      <div className="stat-item">
                        <div className="stat-value">89%</div>
                        <div className="stat-label">Taxa de ROI</div>
                      </div>
                      <div className="stat-item">
                        <div className="stat-value">3.5x</div>
                        <div className="stat-label">Multiplicador</div>
                      </div>
                    </div>
                  </div>
                  <div className="radar-scan"></div>
                </S.TimelineNode>

                <S.TimelineNode style={{ "--index": 7 } as React.CSSProperties}>
                  <div className="badge">EVOLUÇÃO</div>
                  <div className="glow-effect"></div>
                  <div className="node-content">
                    <div className="node-icon">
                      <RiAiGenerate />
                    </div>
                    <h3 className="node-title">IA Generativa</h3>
                    <p className="node-description">
                      Nossa IA proprietária analisa continuamente novos dados e evolui seus modelos preditivos para garantir a máxima precisão.
                    </p>
                    <div className="progress-bar">
                      <div className="progress"></div>
                    </div>
                    <div className="node-stats">
                      <div className="stat-item">
                        <div className="stat-value">24/7</div>
                        <div className="stat-label">Aprendizado</div>
                      </div>
                      <div className="stat-item">
                        <div className="stat-value">2.4M</div>
                        <div className="stat-label">Dados/Hora</div>
                      </div>
                    </div>
                  </div>
                  <div className="radar-scan"></div>
                </S.TimelineNode>
              </S.TimelineMap>

              <S.DataVisualization>
                <div className="data-grid">
                  <div className="data-card">
                    <div className="card-header">
                      <div className="card-icon"><FaChartBar /></div>
                      <h4 className="card-title">Partidas Analisadas</h4>
                    </div>
                    <div className="card-content">
                      <div className="data-value">+15.890</div>
                      <p className="data-description">
                        Partidas analisadas em tempo real esta semana com nosso sistema de IA avançada
                      </p>
                    </div>
                    <div className="card-footer">
                      <span>Atualizado em tempo real</span>
                      <FaBolt color="#2af497" />
                    </div>
                  </div>

                  <div className="data-card">
                    <div className="card-header">
                      <div className="card-icon"><FaChartLine /></div>
                      <h4 className="card-title">Taxa de Acerto</h4>
                    </div>
                    <div className="card-content">
                      <div className="data-value">92.7%</div>
                      <p className="data-description">
                        Taxa média de acerto dos bilhetes inteligentes gerados pelo nosso algoritmo
                      </p>
                    </div>
                    <div className="card-footer">
                      <span>Últimos 30 dias</span>
                      <FaBolt color="#2af497" />
                    </div>
                  </div>

                  <div className="data-card">
                    <div className="card-header">
                      <div className="card-icon"><MdSmartToy /></div>
                      <h4 className="card-title">Modelos de IA</h4>
                    </div>
                    <div className="card-content">
                      <div className="data-value">79</div>
                      <p className="data-description">
                        Modelos de inteligência artificial trabalhando simultaneamente em nossas análises
                      </p>
                    </div>
                    <div className="card-footer">
                      <span>Treinamento contínuo</span>
                      <FaBolt color="#2af497" />
                    </div>
                  </div>

                  <div className="data-card">
                    <div className="card-header">
                      <div className="card-icon"><MdLeaderboard /></div>
                      <h4 className="card-title">Competições</h4>
                    </div>
                    <div className="card-content">
                      <div className="data-value">294</div>
                      <p className="data-description">
                        Ligas e campeonatos globais monitorados 24 horas por dia, 7 dias por semana
                      </p>
                    </div>
                    <div className="card-footer">
                      <span>Cobertura global</span>
                      <FaBolt color="#2af497" />
                    </div>
                  </div>
                </div>
              </S.DataVisualization>

              <S.CTAOutro>
                {/* Efeito de partículas flutuantes */}
                <div className="particles-container" style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  overflow: 'hidden',
                  zIndex: 0,
                }}>
                  {Array.from({ length: 30 }).map((_, index) => (
                    <div key={index} className="particle" style={{
                      position: 'absolute',
                      width: `${Math.random() * 8 + 3}px`,
                      height: `${Math.random() * 8 + 3}px`,
                      borderRadius: '50%',
                      background: `rgba(42, 244, 151, ${0.2 + Math.random() * 0.3})`,
                      top: `${Math.random() * 100}%`,
                      left: `${Math.random() * 100}%`,
                      animation: `floatY ${5 + Math.random() * 10}s infinite ease-in-out`,
                      animationDelay: `${Math.random() * 5}s`,
                      zIndex: 1,
                    }} />
                  ))}
                </div>

                {/* Efeito de grid de fundo */}
                <div className="grid-background" style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  backgroundImage: 'linear-gradient(rgba(42,244,151,0.05) 1px, transparent 1px), linear-gradient(90deg, rgba(42,244,151,0.05) 1px, transparent 1px)',
                  backgroundSize: '20px 20px',
                  animation: 'gridMove 20s infinite linear',
                  zIndex: 0,
                }} />

                <h2>Desbloqueie o Poder dos Dados Agora</h2>
                <p>Junte-se a milhares de apostadores que já estão lucrando com nossa plataforma avançada de análise esportiva.</p>

                <div style={{
                  position: 'relative',
                  display: 'inline-block',
                  margin: '2rem auto',
                }}>
                  {/* Círculos decorativos ao redor do botão */}
                  <div style={{
                    position: 'absolute',
                    top: '-20px',
                    left: '-20px',
                    width: '40px',
                    height: '40px',
                    borderRadius: '50%',
                    border: '2px solid rgba(42, 244, 151, 0.6)',
                    animation: 'pulseTimeline 1.5s infinite ease-in-out',
                  }}></div>

                  <div style={{
                    position: 'absolute',
                    bottom: '-15px',
                    right: '-15px',
                    width: '30px',
                    height: '30px',
                    borderRadius: '50%',
                    background: 'rgba(42, 244, 151, 0.2)',
                    animation: 'pulseTimeline 2s infinite ease-in-out',
                  }}></div>

                  <S.PowerButton
                    ref={buttonRef}
                    onClick={(e) => {
                      createParticles(e);
                      handleRegisterClick();
                    }}
                  >
                    <div className="button-text">
                      <span style={{
                        position: 'relative',
                        display: 'inline-block',
                      }}>
                        COMEÇAR AGORA
                        <span style={{
                          position: 'absolute',
                          top: '-15px',
                          right: '-10px',
                          fontSize: '12px',
                          background: '#121920',
                          color: '#2af497',
                          padding: '2px 6px',
                          borderRadius: '4px',
                          fontWeight: 'bold',
                          transform: 'rotate(15deg)',
                          boxShadow: '0 0 10px rgba(42, 244, 151, 0.5)',
                          animation: 'pulseTimeline 2s infinite ease-in-out',
                        }}>
                          GRÁTIS
                        </span>
                      </span>
                      <span className="button-icon">
                        <FaArrowRight />
                      </span>
                    </div>
                    <div className="particles-container"></div>
                  </S.PowerButton>
                </div>
              </S.CTAOutro>
            </S.TimelineContainer>
          </S.DataTimelineSection>
        </ScrollReveal>

        <S.Footer>
          <p>Precision © Todos os direitos reservados.</p>
          <div className="company-info">
            <p>CLIMB MIDIA LTDA - CNPJ: 50.451.992/0001-41</p>
            <a href="mailto:Contato@climbmidia.com" className="contact">Contato@climbmidia.com</a>
          </div>
        </S.Footer>
      </S.Container>
    </>
  );
};

export default NewLp;